import {Component, Input, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators} from '@angular/forms';
import {EMAIL_REGEXP, ERROR_MSG, PHONE_NUMBER_MASK, UtcListConstant} from '@core/constants';
import {LocationForm, LocationType} from '@core/models';
import {ValidationHelper} from '@core/helpers';
import {Observable} from 'rxjs';
import {BaseApiService} from '@core/api-services';
import * as moment_timezone from 'moment-timezone';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AsyncPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { NgxMaskDirective } from 'ngx-mask';


@Component({
    selector: 'app-location-form',
    templateUrl: './location-form.component.html',
    styleUrls: ['./location-form.component.scss'],
    imports: [MatFormFieldModule, MatInputModule, MatSelectModule, AsyncPipe, MatIconModule, ReactiveFormsModule, NgxMaskDirective]
})
export class LocationFormComponent implements OnInit {
  protected readonly ERROR_MSG = ERROR_MSG;
  @Input('formData') set setFormData($eve: LocationForm) {
        this.isEdit = !!$eve;
        if (!this.isEdit) {
            return;
        }
        const {extra_contacts, ...formData} = $eve;
        this.locationForm.patchValue({
            ...formData,
            location_type_id: +formData.location_type_id,
            phone_number: $eve.phone_number === 'undefined' ? '' : $eve.phone_number,
        });
        (this.extraContactsForm.get('extra_contacts') as FormArray).clear();

        for (const contact of extra_contacts) {
            this.addContact(
                contact.contact_phone_number,
                contact.contact_name,
                contact.contact_email
            );
        }
    }

    private isEdit: boolean;
    public locationTypes$: Observable<LocationType[]> =
        this.baseApiService.locationsService.getLocationTypes();
    utcList: { value: string; id: string; label: string }[] = this._buildUtcList();
    public locationForm: FormGroup = this._createLocationForm();
    public extraContactsForm: FormGroup = this.fb.group({extra_contacts: this.fb.array([])});
    public phoneNumberMask: string = PHONE_NUMBER_MASK;

    constructor(
        private fb: FormBuilder,
        private baseApiService: BaseApiService
    ) {
    }

    ngOnInit(): void {
    }

    /**
     * Builds the timezone dropdown list with each zone's current UTC offset
     * computed live via moment-timezone, instead of a hardcoded value — this
     * stays correct across daylight saving transitions automatically, since
     * moment-timezone already knows each IANA zone's real DST rules.
     */
    private _buildUtcList(): { value: string; id: string; label: string }[] {
        return UtcListConstant.map((zone) => ({
            ...zone,
            label: `${moment_timezone.tz(zone.id).format('Z')} ${zone.value}`,
        }));
    }

    private _createLocationForm(): FormGroup {
        const commonValidators: ValidatorFn[] = [
            Validators.required,
            ValidationHelper.noWhitespaceValidator,
            ValidationHelper.minLengthValidator,
        ];
        return this.fb.group({
            address: ['', commonValidators],
            location_name: ['', commonValidators],
            phone_number: '',
            email: [
                '',
                [
                    Validators.required,
                    ValidationHelper.noWhitespaceValidator,
                    Validators.pattern(EMAIL_REGEXP),
                ],
            ],
            contact_phone_number: '',
            contact_name: [
                '',
                [
                    ValidationHelper.minLengthValidator,
                    ValidationHelper.noWhitespaceValidator,
                ],
            ],
            contact_email: [
                '',
                [
                    ValidationHelper.noWhitespaceValidator,
                    Validators.pattern(EMAIL_REGEXP),
                ],
            ],
            timezone: ['', Validators.required],
            location_type_id: ['', Validators.required],
        });
    }

    public addContact(phone = '', name = '', email = ''): void {
        const formArray = this.extraContactsForm.get('extra_contacts') as FormArray;
        formArray.push(
            this.fb.group({
                contact_phone_number: phone,
                contact_name: [name, [
                    ValidationHelper.minLengthValidator,
                    ValidationHelper.noWhitespaceValidator,
                ]],
                contact_email: [email, [
                    Validators.pattern(EMAIL_REGEXP),
                    ValidationHelper.noWhitespaceValidator,
                ]],
            })
        );
    }

    public removeContact(i: number): void {
        const extraContacts: FormArray = this.extraContactsForm.get(
            'extra_contacts'
        ) as FormArray;
        extraContacts.removeAt(i);
    }
}
